import { useQuery } from "@tanstack/react-query";
import { useFoldersByIds, useItemById } from "components/breadcrumb/hooks/useItemById";
import { useItemBySlug } from "components/breadcrumb/hooks/useItemBySlug";
import { useUser } from "components/breadcrumb/hooks/useUserById";
import { formatName } from "utils";
import { JOB_TYPES } from "components/job/type/constants";
import { SERVICES_CONFIG } from "components/project/services/config";
import { fetchEmployee, getItemFromCollection } from "directus/src/operations";
import { find } from "lodash";
import { useLocation, useParams } from "react-router-dom";

export function useBreadcrumbData() {
  const params = useParams();
  const { "*": splat, folderId: folderIdParam } = useParams();
  const folderPathIds = splat
    ? splat.split("/").filter(Boolean)
    : folderIdParam
      ? [folderIdParam]
      : [];

  const folderPath = useFoldersByIds(folderPathIds);
  const folder = folderPath.data?.length
    ? folderPath.data[folderPath.data.length - 1].name
    : undefined;

  // Extract relevant params
  const {
    project_slug: project_id,
    jobId,
    contractId,
    employeeId,
    milestoneId,
    service_collection,
    quadId,
    billableId,
    taskId,
    surveyId,
    repo_id: repoId,
    branch_name: branchName,
  } = params;
  // Global GitHub integration only; the project-level GitHub route also has a :repo_id param.
  const isGithubIntegration = useLocation().pathname.startsWith("/integrations/github/");

  // Fetch project data
  const project = useItemBySlug("projects", project_id);
  const billable = useItemById("billables", billableId);
  const job = useItemById("jobs", jobId);
  const contract = useItemById("contracts", contractId);
  const quad = useItemById("performance_review_months", quadId);
  const milestone = useItemById("milestones", milestoneId);
  const service = find(SERVICES_CONFIG, { collection: service_collection });
  const typeLabel = JOB_TYPES[job?.type]?.label;

  // Fetch task data (tasks use "title" instead of "name")
  const task = useQuery({
    queryKey: ["tasks", "id", taskId],
    queryFn: async () => {
      const query = {
        fields: ["title"],
      };
      const response = await getItemFromCollection("tasks", taskId, query);
      return response;
    },
    enabled: !!taskId,
    staleTime: 5 * 60 * 1000,
  });

  const survey = useQuery({
    queryKey: ["survey", "id", surveyId],
    queryFn: async () => {
      const query = {
        fields: ["title"],
      };
      const response = await getItemFromCollection("surveys", surveyId, query);
      return response;
    },
    enabled: !!surveyId,
    staleTime: 5 * 60 * 1000,
  });

  const employee = useQuery({
    queryKey: ["breadcrumb", "employee", employeeId],
    queryFn: () => fetchEmployee(employeeId, {
      fields: ["id", "first_name", "last_name"],
    }),
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000,
  });

  const repository = useQuery({
    queryKey: ["breadcrumb", "git_repositories", repoId],
    queryFn: () => getItemFromCollection("git_repositories", repoId, { fields: ["repository_url"] }),
    enabled: !!repoId && isGithubIntegration,
    staleTime: 5 * 60 * 1000,
  });
  return {
    project: project.data?.name,
    // Repo name from https://github.com/org/name.
    repository: repository.data?.repository_url?.replace(/\/+$/, "").split("/").pop(),
    branch: branchName ? decodeURIComponent(branchName) : undefined,
    job: job.data?.name,
    contract: contract.data?.name,
    billable: billable.data?.name,
    quad: quad.data?.name,
    milestone: milestone.data?.name,
    survey: survey.data?.title,
    employee: employee.data ? formatName(employee.data) || undefined : undefined,
    service: service?.title,
    type: typeLabel,
    task: task.data?.title,
    folder,
    folderPathWithNames: folderPath.data ?? [],
    isLoading: {
      project: project.isLoading,
      job: job.isLoading,
      contract: contract.isLoading,
      billable: billable.isLoading,
      quad: quad.isLoading,
      survey: survey.isLoading,
      employee: employee.isLoading,
      milestone: milestone.isLoading,
      task: task.isLoading,
      repository: repository.isLoading,
      folder: folderPath.isLoading,
      any:
        project.isLoading ||
        job.isLoading ||
        contract.isLoading ||
        billable.isLoading ||
        quad.isLoading ||
        employee.isLoading ||
        milestone.isLoading ||
        task.isLoading ||
        survey.isLoading ||
        folderPath.isLoading,
    },

    errors: {
      project: project.error,
      job: job.error,
      contract: contract.error,
      billable: billable.error,
      quad: quad.error,
      employee: employee.error,
      milestone: milestone.error,
      task: task.error,
      survey: survey.error,
      folder: folderPath.error,
      hasAny: !!(
        project.error ||
        job.error ||
        contract.error ||
        billable.error ||
        quad.error ||
        employee.error ||
        milestone.error ||
        task.error ||
        survey.error ||
        folderPath.error
      ),
    },

    getName: (entityType) => {
      const folderData = folderPath.data?.length
        ? folderPath.data[folderPath.data.length - 1]
        : null;
      const data = {
        project: project.data,
        job: job.data,
        contract: contract.data,
        billable: billable.data,
        quad: quad.data,
        employee: employee.data,
        milestone: milestone.data,
        folder: folderData ? { name: folderData.name } : null,
        type: typeLabel,
      }[entityType];

      return getDisplayName(data, `${entityType}s`);
    },
  };
}
